/**
 * One animation clip at runtime, and the library of them.
 *
 * A clip is not just a set of poses. Everything the player can do is a property of some
 * clip, and the selector reasons almost entirely about numbers DERIVED from a clip — how
 * fast it starts and ends, which way it turns, which foot it leaves you on. Those numbers
 * are computed offline by tools/convert/animations.py and arrive here pre-baked; nothing in
 * this file recomputes them. See docs/original-engine/animation-system.md §2.4 and §13.1.
 *
 * The one thing that IS computed on load is the per-frame root position track (§"position
 * cache" below), because the same interpolation is needed for posing anyway and having one
 * code path guarantees the two agree.
 */

import { Quaternion, Vector3 } from 'three';
import { Velocity, floatToEnumVelocity } from './velocity';
import { rotate2D } from '../../core/math';

/** animation.hpp:41-44 */
export enum Foot {
  Left = 0,
  Right = 1,
}

const FORWARD = new Vector3(0, -1, 0);

// ---------------------------------------------------------------------------
// Tracks
// ---------------------------------------------------------------------------

/** A sparse keyframe track. Frames ascend; different nodes key different frames. */
export interface PositionTrack {
  name: string;
  frames: number[];
  /** Flat, 3 per key. */
  values: Float32Array;
}

export interface RotationTrack {
  name: string;
  frames: number[];
  /** Flat, 4 per key, [x, y, z, w]. */
  values: Float32Array;
}

// ---------------------------------------------------------------------------
// The clip
// ---------------------------------------------------------------------------

export interface AnimClip {
  /**
   * Position in the library.
   *
   * Load-bearing, not a convenience. Every sort in the selector is stable, so when two
   * clips score identically all the way down the comparator chain — which happens
   * constantly, because the scores are coarse — the one that came first in the library
   * wins. Reordering the library changes which animation plays.
   */
  index: number;
  name: string;
  /** The clip's `<type>`: movement, ballcontrol, trap, shot, ... */
  animType: string;
  frameCount: number;
  /**
   * animation.hpp:83 — `frameCount - 1`, and the authoritative length for every blending
   * calculation. The last frame of a clip is treated as the same instant as frame 0 of
   * whatever follows it, so it is never played twice.
   */
  effectiveFrameCount: number;

  /** The clip's variable map, values as strings exactly as the engine holds them. */
  vars: Readonly<Record<string, string>>;

  // --- derived, all baked offline ---
  /** m/s. Sprint clips report 7.0 here, not 8.0. See velocity.ts. */
  incomingVelocity: number;
  outgoingVelocity: number;
  incomingVelocityEnum: Velocity;
  outgoingVelocityEnum: Velocity;
  /** m/s, anim space, Z zeroed. */
  incomingMovement: Vector3;
  outgoingMovement: Vector3;
  /** Metres from first root key to last, Z zeroed. */
  translation: Vector3;
  outgoingAngle: number;
  incomingBodyAngle: number;
  outgoingBodyAngle: number;
  outgoingDirection: Vector3;
  incomingBodyDirection: Vector3;
  outgoingBodyDirection: Vector3;
  quadrantId: number;
  currentFoot: Foot;
  outgoingFoot: Foot;
  /** 0..1, how hard this clip is to pull off. Feeds the physics solver's penalties. */
  difficulty: number;
  /** Frame the ball is contacted on, or -1. */
  touchFrame: number;

  /**
   * Where the ball has to be, in the clip's own frame, for the contact to look right.
   *
   * In the shipped data there is exactly one of these per touch clip and none at all
   * otherwise. The engine has machinery to synthesise several more around it, and that
   * machinery is switched off by an early return left in the source
   * (`AddExtraTouches`, animcollection.cpp:910). The list stays a list because the search
   * that consumes it walks it middle-outwards, which only means anything with several.
   */
  touches: { frame: number; position: Vector3 }[];

  /**
   * Which part of the body meets the ball — "left_foot", "trunk", "head", and so on.
   *
   * Not authored: the engine poses a spare skeleton at the touch frame and takes whichever
   * body part is nearest the authored ball position. All the game asks of it is whether the
   * name mentions a foot or a lower leg, because that is what separates a kick from a nudge,
   * and a keeper may not pick up a kick from his own side. Filled in by
   * `annotateTouchBodyParts`; empty until then.
   */
  touchBodyPart: string;

  // --- tracks ---
  root: PositionTrack;
  joints: RotationTrack[];
  /**
   * The root position sampled at EVERY frame, with Z forced to zero — the original's
   * `animPositionCache` (match.cpp:96-101).
   *
   * Z is zeroed here and ONLY here. The sparse `root` track above keeps its Z, which is
   * the player's hip height and is what the renderer uses to make him bob. Zero it once
   * centrally and the player walks like a chess piece.
   */
  positionCache: Vector3[];
}

/** Read a clip variable. A missing one reads as the empty string, as `atof("")` gives 0. */
export function variable(clip: AnimClip, name: string): string {
  return clip.vars[name] ?? '';
}

/** `atof(anim->GetVariable(name))` — missing or unparsable reads as 0. */
export function numericVariable(clip: AnimClip, name: string): number {
  const value = parseFloat(clip.vars[name] ?? '');
  return Number.isNaN(value) ? 0 : value;
}

/**
 * `GetVectorFromString(anim->GetVariable(name))` (base/utils.cpp:222-240).
 *
 * The values are comma-separated and sometimes spaced, sometimes not, because they were
 * authored by hand. A missing variable reads as the zero vector, which every caller treats
 * as "this clip does not declare one".
 */
export function vectorVariable(clip: AnimClip, name: string, out = new Vector3()): Vector3 {
  const raw = clip.vars[name];
  if (!raw) return out.set(0, 0, 0);
  const parts = raw.split(',');
  const at = (i: number): number => {
    const value = parseFloat(parts[i] ?? '');
    return Number.isNaN(value) ? 0 : value;
  };
  return out.set(at(0), at(1), at(2));
}

// ---------------------------------------------------------------------------
// Interpolation — animation.cpp:179-295
// ---------------------------------------------------------------------------

/**
 * `Quaternion::Slerp` (quaternion.cpp:352-401), reproduced rather than delegated.
 *
 * Two reasons not to use three.js's. It returns `from` rather than `to` when the two are
 * already parallel, and — the one that matters — it is deliberately usable with a bias
 * ABOVE 1, which extrapolates past `to`. The original relies on that to continue a clip
 * past its last keyframe (animation.cpp:277-281, where the clamp is commented out).
 */
export function slerpUnclamped(
  from: Quaternion,
  bias: number,
  to: Quaternion,
  out = new Quaternion(),
): Quaternion {
  let bx = to.x;
  let by = to.y;
  let bz = to.z;
  let bw = to.w;
  let cosHalfTheta = from.w * bw + from.x * bx + from.y * by + from.z * bz;
  if (cosHalfTheta < 0) {
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
    cosHalfTheta = -cosHalfTheta;
  }
  if (Math.abs(cosHalfTheta) >= 1.0) return out.copy(from);

  const halfTheta = Math.acos(cosHalfTheta);
  const sinHalfTheta = Math.sqrt(1.0 - cosHalfTheta * cosHalfTheta);
  if (Math.abs(sinHalfTheta) < 0.000001) {
    // The original does NOT normalise this branch. Kept as written.
    return out.set(
      (from.x + bx) * 0.5,
      (from.y + by) * 0.5,
      (from.z + bz) * 0.5,
      (from.w + bw) * 0.5,
    );
  }
  const ratioA = Math.sin((1 - bias) * halfTheta) / sinHalfTheta;
  const ratioB = Math.sin(bias * halfTheta) / sinHalfTheta;
  return out.set(
    from.x * ratioA + bx * ratioB,
    from.y * ratioA + by * ratioB,
    from.z * ratioA + bz * ratioB,
    from.w * ratioA + bw * ratioB,
  );
}

/** Index of the last key at or before `frame`, or -1 if `frame` precedes every key. */
function lastKeyAtOrBefore(frames: number[], frame: number): number {
  let low = 0;
  let high = frames.length - 1;
  let found = -1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (frames[mid]! <= frame) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return found;
}

/**
 * `GetInterpolatedValues` for a position track (animation.cpp:179-295, position path).
 *
 * Three regimes, all of which occur:
 *   inside the clip  — linear blend of the two keys that bracket the frame
 *   past the end     — LINEAR EXTRAPOLATION, deliberately not clamped
 *   before the start — hold the first key
 */
export function samplePosition(
  track: PositionTrack,
  frameCount: number,
  frame: number,
  out = new Vector3(),
): Vector3 {
  const { frames, values } = track;
  const at = (i: number): Vector3 =>
    new Vector3(values[i * 3]!, values[i * 3 + 1]!, values[i * 3 + 2]!);

  if (frames.length === 0) return out.set(0, 0, 0);

  if (frame > 0 && frame < frameCount) {
    const before = lastKeyAtOrBefore(frames, frame - 1);
    // The original keeps at most the last key strictly before `frame` and the first at
    // or after it; a node whose keys all end early simply holds its last pose.
    const firstAtOrAfter = before + 1;
    if (firstAtOrAfter >= frames.length) return out.copy(at(frames.length - 1));
    if (before < 0) return out.copy(at(firstAtOrAfter));

    const f0 = frames[before]!;
    const f1 = frames[firstAtOrAfter]!;
    const distance = f1 - f0;
    const influence0 = 1 - (frame - f0) / distance;
    const influence1 = 1 - (f1 - frame) / distance;
    const a = at(before);
    const b = at(firstAtOrAfter);
    return out.set(
      a.x * influence0 + b.x * influence1,
      a.y * influence0 + b.y * influence1,
      a.z * influence0 + b.z * influence1,
    );
  }

  if (frame >= frameCount) {
    if (frames.length === 1) return out.copy(at(0));
    const last = frames.length - 1;
    const gap = frames[last]! - frames[last - 1]!;
    const bias = 1 + (frame - frames[last]!) / gap;
    const a = at(last - 1);
    const b = at(last);
    return out.set(
      a.x * (1 - bias) + b.x * bias,
      a.y * (1 - bias) + b.y * bias,
      a.z * (1 - bias) + b.z * bias,
    );
  }

  return out.copy(at(0));
}

/** `GetInterpolatedValues` for a rotation track — same three regimes, slerp instead. */
export function sampleRotation(
  track: RotationTrack,
  frameCount: number,
  frame: number,
  out = new Quaternion(),
): Quaternion {
  const { frames, values } = track;
  const at = (i: number, into: Quaternion): Quaternion =>
    into.set(values[i * 4]!, values[i * 4 + 1]!, values[i * 4 + 2]!, values[i * 4 + 3]!);

  if (frames.length === 0) return out.set(0, 0, 0, 1);

  const scratch = new Quaternion();

  if (frame > 0 && frame < frameCount) {
    const before = lastKeyAtOrBefore(frames, frame - 1);
    const firstAtOrAfter = before + 1;
    if (firstAtOrAfter >= frames.length) return at(frames.length - 1, out);
    if (before < 0) return at(firstAtOrAfter, out);

    const f0 = frames[before]!;
    const f1 = frames[firstAtOrAfter]!;
    const bias = (frame - f0) / (f1 - f0);
    return slerpUnclamped(at(before, scratch), bias, at(firstAtOrAfter, new Quaternion()), out);
  }

  if (frame >= frameCount) {
    if (frames.length === 1) return at(0, out);
    const last = frames.length - 1;
    const gap = frames[last]! - frames[last - 1]!;
    const bias = 1 + (frame - frames[last]!) / gap;
    return slerpUnclamped(at(last - 1, scratch), bias, at(last, new Quaternion()), out);
  }

  return at(0, out);
}

/**
 * The per-frame root position track — `Match::GetAnimPositionCache` (match.cpp:90-105).
 *
 * One entry per frame, sampled through the same interpolator the renderer uses, with the
 * height forced to zero. Note the loop stops at `frameCount - 1`, so the extrapolation
 * regime is never reached from here.
 */
export function buildPositionCache(root: PositionTrack, frameCount: number): Vector3[] {
  const cache: Vector3[] = [];
  for (let frame = 0; frame < frameCount; frame++) {
    const position = samplePosition(root, frameCount, frame);
    position.z = 0;
    cache.push(position);
  }
  return cache;
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

interface BundleDerived {
  animType: string;
  incomingVelocity: number;
  outgoingVelocity: number;
  incomingMovement: [number, number, number];
  outgoingMovement: [number, number, number];
  translation: [number, number, number];
  outgoingAngle: number;
  incomingBodyAngle: number;
  outgoingBodyAngle: number;
  quadrantId: number;
  currentFoot: number;
  outgoingFoot: number;
  difficulty: number;
  touchFrame: number;
}

interface BundleClip {
  name: string;
  source: string;
  mirrored: boolean;
  generated: boolean;
  jointSet: number;
  frameCount: number;
  byteOffset: number;
  byteLength: number;
  keyCounts: number[];
  extension?: { name: string; keyCount: number };
  vars: Record<string, string>;
  derived: BundleDerived;
}

export interface AnimBundleIndex {
  format: string;
  formatVersion: number;
  buffer: string;
  jointSets: string[][];
  clips: BundleClip[];
}

/**
 * Decode the whole clip library from one binary and its index.
 *
 * Layout, per clip block: every track's keyframe NUMBERS first as 16-bit integers, then
 * every track's VALUES as 32-bit floats — not interleaved per track. Track 0 is the root
 * and carries positions (3 floats per key); the rest are joints and carry quaternions
 * (4 floats). That asymmetry is the original's, at animation.cpp:1085-1102.
 */
export function loadLibrary(index: AnimBundleIndex, buffer: ArrayBuffer): AnimClip[] {
  if (index.format !== 'GFBANIM2') {
    throw new Error(
      `Animation bundle is "${index.format}", expected "GFBANIM2". Re-run tools/convert/animations.py.`,
    );
  }

  return index.clips.map((entry, clipIndex) => {
    const jointNames = index.jointSets[entry.jointSet];
    if (!jointNames) {
      throw new Error(`Clip "${entry.name}" names joint set ${entry.jointSet}, which is absent`);
    }

    const extensionKeys = entry.extension ? entry.extension.keyCount : 0;
    const keyTotal = entry.keyCounts.reduce((a, b) => a + b, 0) + extensionKeys;
    const frames = new Uint16Array(buffer, entry.byteOffset, keyTotal);
    // Values start at the next 4-byte boundary after the frame numbers.
    const valueOffset = entry.byteOffset + ((keyTotal * 2 + 3) & ~3);
    const valueTotal =
      entry.keyCounts[0]! * 3 +
      entry.keyCounts.slice(1).reduce((a, b) => a + b, 0) * 4 +
      extensionKeys * 3;
    const values = new Float32Array(buffer, valueOffset, valueTotal);

    let frameCursor = 0;
    let valueCursor = 0;

    const rootCount = entry.keyCounts[0]!;
    const root: PositionTrack = {
      name: jointNames[0]!,
      frames: Array.from(frames.subarray(frameCursor, frameCursor + rootCount)),
      values: values.slice(valueCursor, valueCursor + rootCount * 3),
    };
    frameCursor += rootCount;
    valueCursor += rootCount * 3;

    const joints: RotationTrack[] = [];
    for (let track = 1; track < entry.keyCounts.length; track++) {
      const count = entry.keyCounts[track]!;
      joints.push({
        name: jointNames[track]!,
        frames: Array.from(frames.subarray(frameCursor, frameCursor + count)),
        values: values.slice(valueCursor, valueCursor + count * 4),
      });
      frameCursor += count;
      valueCursor += count * 4;
    }

    // The ball-contact track, written last by the converter: a frame number and the
    // position the ball must occupy in the clip's own coordinates.
    const touches: { frame: number; position: Vector3 }[] = [];
    for (let i = 0; i < extensionKeys; i++) {
      touches.push({
        frame: frames[frameCursor + i]!,
        position: new Vector3(
          values[valueCursor + i * 3]!,
          values[valueCursor + i * 3 + 1]!,
          values[valueCursor + i * 3 + 2]!,
        ),
      });
    }
    touches.sort((a, b) => a.frame - b.frame);

    const d = entry.derived;
    const outgoingDirection = rotate2D(FORWARD, d.outgoingAngle);

    return {
      index: clipIndex,
      name: entry.name,
      animType: d.animType,
      frameCount: entry.frameCount,
      effectiveFrameCount: entry.frameCount - 1,
      vars: entry.vars,

      incomingVelocity: d.incomingVelocity,
      outgoingVelocity: d.outgoingVelocity,
      incomingVelocityEnum: floatToEnumVelocity(d.incomingVelocity),
      outgoingVelocityEnum: floatToEnumVelocity(d.outgoingVelocity),
      incomingMovement: new Vector3(...d.incomingMovement),
      outgoingMovement: new Vector3(...d.outgoingMovement),
      translation: new Vector3(...d.translation),
      outgoingAngle: d.outgoingAngle,
      incomingBodyAngle: d.incomingBodyAngle,
      outgoingBodyAngle: d.outgoingBodyAngle,
      outgoingDirection,
      incomingBodyDirection: rotate2D(FORWARD, d.incomingBodyAngle),
      // animation.cpp:877-891 — both of these are just forward rotated by the matching
      // angle. The OUTGOING one is therefore relative to the direction of travel, not an
      // absolute heading; the callers that want an absolute heading rotate the clip's own
      // outgoing angle in themselves. Adding it here instead double-counts the turn.
      outgoingBodyDirection: rotate2D(FORWARD, d.outgoingBodyAngle),
      quadrantId: d.quadrantId,
      currentFoot: d.currentFoot as Foot,
      outgoingFoot: d.outgoingFoot as Foot,
      difficulty: d.difficulty,
      touchFrame: d.touchFrame,
      touches,
      touchBodyPart: '',

      root,
      joints,
      positionCache: buildPositionCache(root, entry.frameCount),
    };
  });
}
